import React, { useState, useEffect, memo, useCallback } from "react";
import ReactDOM from "react-dom";
import {
  Plus,
  Code,
  Eye,
  Trash2,
  ChevronRight,
  ChevronDown,
  X,
  Check,
  Database,
  Box,
  CheckSquare,
  Square,
  Asterisk,
  CheckCircle,
} from "lucide-react";
import { toast } from "react-hot-toast";
import Tooltip from "../../../widgets/ToolTip";

// --- Interfaces ---

export interface ZodValidation {
  min?: number;
  max?: number;
  length?: number;
  email?: boolean;
  url?: boolean;
  uuid?: boolean;
  regex?: string;
}

export interface SchemaProperty {
  id: string;
  name: string;
  type: string; // string, number, boolean, array, object, date, enum
  description: string;
  required: boolean; // default true in Zod
  isArray: boolean; // shortcut for z.array(z.type())
  validation?: ZodValidation;
  children?: SchemaProperty[];
  isExpanded?: boolean;
  enumValues?: EnumValue[];
  singleValue?: boolean;
}

interface EnumValue {
  id: string;
  value: string;
}

interface SchemaBuilderModalProps {
  initialSchema: any;
  isOpen: boolean;
  onClose: () => void;
  onSave: (schema: any) => void;
}

// --- Helper Components ---

// --- Property Row Component ---
const PropertyRow = memo(
  ({
    property,
    level = 0,
    updateProperty,
    removeProperty,
    addProperty,
    toggleExpand,
  }: {
    property: SchemaProperty;
    level?: number;
    updateProperty: (
      id: string,
      field: keyof SchemaProperty,
      value: any
    ) => void;
    removeProperty: (id: string) => void;
    addProperty: (parentId?: string) => void;
    toggleExpand: (id: string) => void;
  }) => {
    const hasChildren = property.type === "object" || (property.type === "array" && !property.isArray);

    const paddingLeft = level * 24;
    const [showSettings, ] = useState(false);

    const handleAddEnumValue = () => {
        const newEnumValue: EnumValue = { id: Date.now().toString() + Math.random(), value: "" };
        const updatedEnumValues = [...(property.enumValues || []), newEnumValue];
        updateProperty(property.id, "enumValues", updatedEnumValues);
    };

    const handleUpdateEnumValue = (enumId: string, newValue: string) => {
        const updatedEnumValues = (property.enumValues || []).map(enumItem =>
          enumItem.id === enumId ? { ...enumItem, value: newValue } : enumItem
        );
        updateProperty(property.id, "enumValues", updatedEnumValues);
    };

    const handleRemoveEnumValue = (enumId: string) => {
        const updatedEnumValues = (property.enumValues || []).filter(enumItem => enumItem.id !== enumId);
        updateProperty(property.id, "enumValues", updatedEnumValues);
    };


    return (
      <div className="group bg-white border-b border-slate-100 last:border-0 transition-all duration-200">
        <div className={`flex items-center py-2 px-4 gap-3 hover:bg-slate-50 ${showSettings ? 'bg-slate-50' : ''}`}>
          {/* Column 1: Field Key (Tree Structure) */}
          <div className="w-[240px] flex-shrink-0 flex items-center relative pt-1">
            <div
              style={{ paddingLeft: `${paddingLeft}px` }}
              className="flex items-center w-full relative"
            >
              {/* Lines */}
              {level > 0 && (
                <>
                  <div className="absolute border-l border-slate-200 h-full top-0" style={{ left: `${paddingLeft - 12}px` }} />
                  <div className="absolute border-b border-slate-200 w-3 top-1/2" style={{ left: `${paddingLeft - 12}px` }} />
                </>
              )}

              {/* Expander */}
              <div className="w-6 flex-shrink-0 flex justify-center mr-1 z-10 pt-1">
                {hasChildren ? (
                  <button
                    onClick={() => toggleExpand(property.id)}
                    className="p-0.5 text-slate-400 hover:text-slate-700 rounded transition-colors"
                  >
                    {property.isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  </button>
                ) : (
                  <div className="w-1.5 h-1.5 rounded-full bg-slate-200 mx-auto" />
                )}
              </div>

              <input
                type="text"
                placeholder="field_name"
                value={property.name}
                onChange={(e) => updateProperty(property.id, "name", e.target.value)}
                className="w-full px-2 py-1.5 bg-transparent border border-transparent rounded hover:border-slate-300 focus:bg-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/10 text-sm font-mono font-medium text-slate-700 placeholder:text-slate-300 transition-all duration-200 outline-none"
              />
            </div>
          </div>

          {/* Column 2: Type Selector */}
          <div className="w-[120px] flex-shrink-0">
             <div className="relative group/type">
               <select
                 value={property.type}
                 onChange={(e) => updateProperty(property.id, "type", e.target.value)}
                 className="w-full pl-3 pr-6 py-1.5 bg-slate-50 border border-slate-200 rounded-lg hover:border-slate-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/10 text-xs font-semibold text-slate-700 appearance-none cursor-pointer outline-none transition-all"
               >
                 <option value="string">String</option>
                 <option value="number">Number</option>
                 <option value="boolean">Boolean</option>
                 <option value="date">Date</option>
                 <option value="object">Object</option>
                 <option value="array">Array</option>
                 <option value="enum">Enum</option>
               </select>
               <ChevronDown size={12} className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400" />
             </div>
          </div>

          {/* Column 3: Description */}
          <div className="flex-1 min-w-[150px]">
            <input 
              type="text" 
              value={property.description} 
              onChange={(e) => updateProperty(property.id, "description", e.target.value)}
              placeholder="Description..."
              className="w-full px-2 py-1.5 bg-transparent border border-transparent rounded hover:border-slate-300 focus:bg-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/10 text-xs text-slate-600 placeholder:text-slate-300 transition-all duration-200 outline-none truncate focus:overflow-visible"
            />
          </div>

          {/* Column 4: Flags (Optional/SingleValue) */}
          <div className="w-[140px] flex items-center gap-2 justify-center">
            <button
                onClick={() => updateProperty(property.id, "required", !property.required)}
                type="button"
                className={`flex items-center justify-center w-8 h-8 rounded-full transition-colors ${property.required
                    ? "bg-blue-600 text-white"
                    : "bg-gray-200 text-gray-600 hover:bg-blue-100"
                    }`}
                >
                <Tooltip content="Required">
                    <Asterisk className="w-4 h-4" />
                </Tooltip>
            </button>

            <button
                onClick={() => updateProperty(property.id, "singleValue", !property.singleValue)}
                type="button"
                className={`flex items-center justify-center w-8 h-8 rounded transition-colors ${property.singleValue
                    ? "bg-blue-600 text-white"
                    : "bg-gray-200 text-gray-600 hover:bg-blue-100"
                    }`}
                >
                <Tooltip content="Make property a single value" position="left">
                    {property.singleValue ? (
                    <CheckSquare className="w-4 h-4" />
                    ) : (
                    <Square className="w-4 h-4" />
                    )}
                </Tooltip>
            </button>
          </div>

          {/* Column 4: Actions */}
          <div className="w-[100px] flex justify-end gap-1">
             
            {(property.type === 'object' || property.type === 'array') && (
              <button
                onClick={() => addProperty(property.id)}
                className="p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-md transition-colors"
                title="Add Child Field"
              >
                <Plus size={14} />
              </button>
            )}
            <button
              onClick={() => removeProperty(property.id)}
              className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors"
              title="Delete Field"
            >
              <Trash2 size={14} />
            </button>
          </div>
        </div>

        {/* Dynamic list for Enum Values */}
        {property.type === "enum" && (
            <div className="px-3 pb-3 pl-12 space-y-2 bg-slate-50 border-t border-slate-100 p-2">
            {property.enumValues?.map((enumItem) => (
                <div key={enumItem.id} className="flex items-center gap-2">
                <input
                    type="text"
                    placeholder="Enum value"
                    value={enumItem.value}
                    onChange={(e) => handleUpdateEnumValue(enumItem.id, e.target.value)}
                    className="flex-grow px-3 py-1.5 text-xs bg-white border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none"
                />
                <Tooltip content="Delete">
                <button
                    onClick={() => handleRemoveEnumValue(enumItem.id)}
                    type="button"
                    className="w-7 h-7 flex-shrink-0 text-gray-400 rounded-md hover:bg-red-50 hover:text-red-500 flex items-center justify-center"
                >
                    <Trash2 className="w-3.5 h-3.5" />
                </button>
                </Tooltip>
                </div>
            ))}
            <button
                onClick={handleAddEnumValue}
                className="flex items-center gap-1 mt-2 text-xs text-blue-600 hover:text-blue-800"
                type="button"
            >
                <Plus className="w-3.5 h-3.5" />
                Add enum value
            </button>
            </div>
        )}

        {/* Expanded Settings Panel */}
        {showSettings && (
           <div className="px-4 py-3 bg-slate-50 border-t border-slate-100 flex gap-6 animate-in slide-in-from-top-1 duration-200">
              <div className="flex-1 space-y-2">
                 <label className="text-[10px] uppercase font-bold text-slate-500 flex items-center gap-1.5">
                    <Check size={10} /> Validations
                 </label>
                 <div className="grid grid-cols-2 gap-2">
                    {/* String Validations */}
                    {property.type === 'string' && (
                        <>
                           <label className="flex items-center gap-2 cursor-pointer">
                              <input 
                                type="checkbox" 
                                checked={property.validation?.email || false} 
                                onChange={(e) => updateProperty(property.id, "validation", { ...property.validation, email: e.target.checked })}
                                className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                              />
                              <span className="text-xs text-slate-600">Email</span>
                           </label>
                           <label className="flex items-center gap-2 cursor-pointer">
                              <input 
                                type="checkbox" 
                                checked={property.validation?.url || false} 
                                onChange={(e) => updateProperty(property.id, "validation", { ...property.validation, url: e.target.checked })}
                                className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                              />
                              <span className="text-xs text-slate-600">URL</span>
                           </label>
                        </>
                    )}
                    
                    {/* Number Validations */}
                    {property.type === 'number' && (
                        <div className="col-span-2 grid grid-cols-2 gap-2">
                           <div>
                              <span className="text-[10px] text-slate-500 block mb-0.5">Min</span>
                              <input 
                                type="number" 
                                value={property.validation?.min || ''}
                                onChange={(e) => updateProperty(property.id, "validation", { ...property.validation, min: e.target.value ? Number(e.target.value) : undefined })}
                                className="w-full text-xs p-1 border rounded"
                              />
                           </div>
                           <div>
                              <span className="text-[10px] text-slate-500 block mb-0.5">Max</span>
                              <input 
                                type="number" 
                                value={property.validation?.max || ''}
                                onChange={(e) => updateProperty(property.id, "validation", { ...property.validation, max: e.target.value ? Number(e.target.value) : undefined })}
                                className="w-full text-xs p-1 border rounded"
                              />
                           </div>
                        </div>
                    )}
                 </div>
              </div>
           </div>
        )}

        {/* Recursion for Children */}
        {hasChildren &&
          property.isExpanded &&
          property.children &&
          property.children.length > 0 && (
            <div className="border-t border-slate-100 bg-slate-50/30">
              {property.children.map((child) => (
                <PropertyRow
                  key={child.id}
                  property={child}
                  level={level + 1}
                  updateProperty={updateProperty}
                  removeProperty={removeProperty}
                  addProperty={addProperty}
                  toggleExpand={toggleExpand}
                />
              ))}
            </div>
          )}
      </div>
    );
  }
);
PropertyRow.displayName = "PropertyRow";

// --- Main Modal Component ---
export const SchemaBuilderModal: React.FC<SchemaBuilderModalProps> = ({
  initialSchema,
  isOpen,
  onClose,
  onSave,
}) => {
  const [activeTab, setActiveTab] = useState<"visual" | "code">("visual");
  const [properties, setProperties] = useState<SchemaProperty[]>([]);
  const [codeValue, setCodeValue] = useState("");

  // --- Parser & Generator Logic ---

  // 1. Parser (JSON Schema -> Internal Property Tree)
  const parseSchemaToProperties = useCallback((schema: any, requiredFields: string[] = []): SchemaProperty[] => {
    if (!schema || !schema.properties) return [];
    
    // Support property ordering if present
    const keys = schema.propertyOrdering || Object.keys(schema.properties);

    return keys.map((key: string) => {
        const value = schema.properties[key];
        if (!value) return null;

        let type = "string";
        let children: SchemaProperty[] | undefined;
        let enumValues: EnumValue[] | undefined;
        let isArray = false;
        let validation: ZodValidation = {};

        // Case 1: Array of objects
        if (value.type === "array" && value.items?.properties) {
          type = "array"; // we will mark this as complex array
          children = parseSchemaToProperties(value.items, value.items.required || []);
        }
        // Case 2: Simple object
        else if (value.type === "object" && value.properties) {
          type = "object";
          children = parseSchemaToProperties(value, value.required || []);
        }
        // Case 3: Array of primitives (string, number, boolean, or enum)
        else if (value.type === "array" && value.items) {
          if (Array.isArray(value.items.enum)) {
             type = "enum";
             enumValues = value.items.enum.map((val: string) => ({ id: Math.random().toString(), value: val }));
          } else {
             type = value.items.type || "string";
          }
          // But wait, the previous Zod builder used isArray flag.
          // Let's unify. If we want z.array(z.string()), that is type=string, isArray=true.
          isArray = true;
          // Note: TemplateForm used 'singleValue' to denote "Array of Primitives".
          // We will map 'singleValue' prop to 'isArray' here to keep consistency with the new Zod Builder interface?
          // The user asked for "same schema builder" as TemplateForm.
          // TemplateForm uses 'singleValue' checkbox to toggle wrapping in array. 
          
          // Let's use the 'singleValue' prop name to match user mental model if they want EXACT match.
          // But I'll stick to 'isArray' internally if it makes sense, OR rename to match TemplateForm fully.
          // Let's map schema array -> isArray property for standard primitives.
        }
        // Case 4: Simple enum
        else if (Array.isArray(value.enum)) {
          type = "enum";
          enumValues = value.enum.map((val: string) => ({ id: Math.random().toString(), value: val }));
        }
        // Case 5: Simple primitive
        else {
          type = value.type || "string";
        }

        if (value.validation) validation = value.validation;

        return {
            id: Math.random().toString(),
            name: key,
            type,
            description: value.description || "",
            required: requiredFields.includes(key),
            isArray,
            validation,
            children,
            enumValues,
            isExpanded: true,
            singleValue: isArray // Mapping array back to singleValue (which acts as 'is array' checkbox)
        };
    }).filter(Boolean) as SchemaProperty[];
  }, []);

  // 2. Generator (Internal Property Tree -> Output Config)
  const generateSchemaConfig = useCallback((props: SchemaProperty[]): any => {
    const schema: any = {
        type: "object",
        properties: {},
        propertyOrdering: [],
        required: [],
    };
    
    props.forEach(p => {
        if (!p.name) return;
        schema.propertyOrdering.push(p.name);
        if (p.required) schema.required.push(p.name);

        let propSchema: any = {
           description: p.description
        };

        if (p.validation && Object.keys(p.validation).length > 0) {
            propSchema.validation = p.validation;
        }

        // Handle Types
        if (p.type === 'object') {
             propSchema.type = 'object';
             const childSchema = generateSchemaConfig(p.children || []);
             propSchema.properties = childSchema.properties;
             propSchema.required = childSchema.required;
             propSchema.propertyOrdering = childSchema.propertyOrdering;
        } else if (p.type === 'array') {
             // Nested array of objects (if children exist) or just array type
             // If it has children, we assume array of objects
             if (p.children && p.children.length > 0) {
                 propSchema.type = 'array';
                 propSchema.items = generateSchemaConfig(p.children || []);
                 // remove root props from items since generateSchemaConfig returns full object schema
             } else {
                 propSchema.type = 'array';
                 propSchema.items = { type: 'string' }; // fallback
             }
        } else if (p.type === 'enum') {
             propSchema.type = 'string';
             propSchema.enum = p.enumValues?.map(e => e.value).filter(v => v) || [];
        } else {
             propSchema.type = p.type;
        }

        // Handle "Single Value" which acts as "Is Array" for primitives
        // naming in TemplateForm was confusing: "singleValue" toggled array wrapping. 
        if (p.singleValue && !['object', 'array'].includes(p.type)) {
             schema.properties[p.name] = {
                 type: 'array',
                 items: propSchema
             };
        } else {
             schema.properties[p.name] = propSchema;
        }
    });

    if (schema.required.length === 0) delete schema.required;

    return schema;
  }, []);

  useEffect(() => {
    if (isOpen) {
      // TemplateForm logic expects input like { type: 'object', properties: {...} }
      const props = parseSchemaToProperties(initialSchema || { type: 'object', properties: {} }, initialSchema?.required || []);
      setProperties(props);
      // We don't setCodeValue here immediately to avoid conflict if we want distinct sync
    }
  }, [isOpen, initialSchema, parseSchemaToProperties]);

  // Sync code value when properties change
  useEffect(() => {
      const config = generateSchemaConfig(properties);
      setCodeValue(JSON.stringify(config, null, 2));
  }, [properties, generateSchemaConfig]);

  const handleApplyCodeChanges = () => {
    try {
      const parsed = JSON.parse(codeValue);
      // Basic validation
      if (typeof parsed !== 'object' || parsed === null) throw new Error("Root must be an object");
      
      const newProps = parseSchemaToProperties(parsed, parsed.required || []);
      setProperties(newProps);
      toast.success("Schema updated from JSON");
    } catch (e: any) {
      toast.error("Invalid JSON: " + e.message);
    }
  };

  // Handler for adding new root or child property
  const addProperty = useCallback((parentId?: string) => {
    const newProp: SchemaProperty = {
      id: Math.random().toString(),
      name: "",
      type: "string",
      description: "",
      required: false,
      isArray: false,
      singleValue: false,
      isExpanded: true,
      enumValues: [],
    };

    if (!parentId) {
      setProperties((prev) => [...prev, newProp]);
    } else {
      setProperties((prev) => {
        const addDeep = (list: SchemaProperty[]): SchemaProperty[] => {
            return list.map(p => {
                if (p.id === parentId) {
                    return { ...p, children: [...(p.children || []), newProp] };
                }
                if (p.children) {
                    return { ...p, children: addDeep(p.children) };
                }
                return p;
            });
        };
        return addDeep(prev);
      });
    }
  }, []);

  // Generic update handler
  const updateProperty = useCallback((id: string, field: keyof SchemaProperty, value: any) => {
    setProperties(prev => {
        const updateDeep = (list: SchemaProperty[]): SchemaProperty[] => {
            return list.map(p => {
                if (p.id === id) {
                    const updated = { ...p, [field]: value };
                    // Reset children if type changes to non-container
                    if (field === 'type') {
                        if (value !== 'object' && value !== 'array') {
                            updated.children = undefined;
                        } else if (!updated.children) {
                            updated.children = [];
                        }
                        
                        if (value === 'enum') {
                            if (!updated.enumValues) updated.enumValues = [];
                        } else {
                            updated.enumValues = undefined;
                        }
                    }
                    return updated;
                }
                if (p.children) {
                    return { ...p, children: updateDeep(p.children) };
                }
                return p;
            });
        };
        return updateDeep(prev);
    });
  }, []);

  const removeProperty = useCallback((id: string) => {
    setProperties(prev => {
        const removeDeep = (list: SchemaProperty[]): SchemaProperty[] => {
            return list.filter(p => p.id !== id).map(p => ({
                ...p,
                children: p.children ? removeDeep(p.children) : undefined
            }));
        };
        return removeDeep(prev);
    });
  }, []);

  const toggleExpand = useCallback((id: string) => {
    setProperties(prev => {
        const toggleDeep = (list: SchemaProperty[]): SchemaProperty[] => {
            return list.map(p => {
                if (p.id === id) return { ...p, isExpanded: !p.isExpanded };
                if (p.children) return { ...p, children: toggleDeep(p.children) };
                return p;
            });
        };
        return toggleDeep(prev);
    });
  }, []);

  const handleSave = () => {
     const config = generateSchemaConfig(properties);
     onSave(config);
     onClose();
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-white rounded-xl shadow-2xl w-[95vw] max-w-[1200px] h-[85vh] flex flex-col overflow-hidden border border-slate-200 ring-1 ring-black/5">
        
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-white shadow-sm z-10">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg shadow-sm text-white">
              <Database size={20} />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-800 tracking-tight">
                Zod Schema Builder
              </h2>
              <p className="text-xs text-slate-500 font-medium">
                Define strictly typed data structures
              </p>
            </div>
          </div>

          <div className="flex items-center gap-4">
             {/* Tabs */}
             <div className="flex bg-slate-100 p-1 rounded-lg border border-slate-200">
               <button
                 onClick={() => setActiveTab("visual")}
                 className={`px-4 py-1.5 text-xs font-semibold rounded-md transition-all flex items-center gap-1.5 ${activeTab === 'visual' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
               >
                 <Eye size={14} /> Visual
               </button>
               <button
                 onClick={() => setActiveTab("code")}
                 className={`px-4 py-1.5 text-xs font-semibold rounded-md transition-all flex items-center gap-1.5 ${activeTab === 'code' ? 'bg-white text-purple-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
               >
                 <Code size={14} /> JSON
               </button>
             </div>
             <button onClick={onClose} className="p-2 text-slate-400 hover:bg-red-50 hover:text-red-500 rounded-lg transition-colors">
               <X size={20} />
             </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden bg-slate-50/50">
           {activeTab === 'visual' ? (
              <div className="flex flex-col h-full">
                 <div className="flex-1 overflow-y-auto p-4 custom-scrollbar">
                    {properties.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-full border-2 border-dashed border-slate-200 rounded-2xl bg-white/50">
                           <div className="p-4 bg-indigo-50 rounded-full mb-4">
                              <Box size={32} className="text-indigo-400" />
                           </div>
                           <h3 className="text-slate-800 font-bold mb-2">Start defining your schema</h3>
                           <p className="text-slate-500 text-sm mb-6 max-w-xs text-center">
                              Create a structured Zod schema for type-safe data extraction.
                           </p>
                           <button
                             onClick={() => addProperty()}
                             className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition font-medium text-sm flex items-center gap-2"
                           >
                             <Plus size={16} /> Add First Field
                           </button>
                        </div>
                    ) : (
                        <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
                           <div className="flex items-center px-4 py-2.5 bg-slate-50 border-b border-slate-200 text-[10px] font-bold text-slate-500 uppercase tracking-widest gap-3">
                              <div className="w-[240px] pl-6">Property Name</div>
                              <div className="w-[120px]">Type</div>
                              <div className="flex-1">Description</div>
                              <div className="w-[140px]">Options</div>
                              <div className="w-[100px] text-right pr-2">Actions</div>
                           </div>
                           <div className="divide-y divide-slate-100">
                             {properties.map(p => (
                                <PropertyRow
                                  key={p.id}
                                  property={p}
                                  updateProperty={updateProperty}
                                  removeProperty={removeProperty}
                                  addProperty={addProperty}
                                  toggleExpand={toggleExpand}
                                />
                             ))}
                           </div>
                        </div>
                    )}

                    {properties.length > 0 && (
                       <button
                         onClick={() => addProperty()}
                         className="mt-4 w-full py-3 border border-dashed border-slate-300 rounded-lg text-slate-500 hover:border-indigo-400 hover:text-indigo-600 hover:bg-indigo-50/30 transition flex items-center justify-center gap-2 text-sm font-medium"
                       >
                         <Plus size={16} /> Add Root Property
                       </button>
                    )}
                 </div>
              </div>
           ) : (
              <div className="h-full relative flex flex-col">
                 <div className="flex-1 relative">
                    <textarea
                        value={codeValue}
                        onChange={(e) => setCodeValue(e.target.value)}
                        className="w-full h-full p-6 font-mono text-sm bg-slate-900 text-emerald-400 resize-none focus:outline-none"
                        spellCheck={false}
                    />
                 </div>
                 <div className="absolute bottom-4 right-6">
                    <button 
                        onClick={handleApplyCodeChanges}
                        className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg shadow-lg transition-colors font-medium text-xs"
                    >
                        <CheckCircle size={14} /> Apply Changes
                    </button>
                 </div>
              </div>
           )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-100 bg-white flex justify-end gap-3 z-10">
           <button onClick={onClose} className="px-5 py-2 text-slate-600 hover:bg-slate-50 rounded-lg font-medium text-sm transition-colors">
              Cancel
           </button>
           <button 
             onClick={handleSave}
             className="px-6 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg shadow-md hover:shadow-indigo-500/25 font-semibold text-sm transition-all flex items-center gap-2"
           >
             <Check size={16} /> Save Schema
           </button>
        </div>

      </div>
    </div>,
    document.body
  );
};
